// Upload-pipeline orchestrator. Glues fake-content + api together.
//
// Stage progression — caller subscribes via `onProgress` to drive the UI:
//
//   "pending"   → file is enqueued, not yet PUT
//   "uploading" → fetch is in-flight
//   "done"      → 2xx received
//   "error"     → still failing after the transient-fault retries, or a
//                 non-retryable answer (e.g. 403 on an expired URL)
//
// One queued item per declared filename; processed in parallel with a
// small concurrency cap to avoid hammering the browser's connection pool.
// Filenames are flat (the presigned-uploads endpoint regex forbids slashes)
// and treated as opaque keys in the lookup map.

import {
  ApiError,
  isRetryableUploadError,
  requestPresignedUploads,
  uploadFile,
} from "./api";
import type { FakeFile } from "./fake-content";
import { inferContentType } from "./fake-content";

export type ItemStatus = "pending" | "uploading" | "done" | "error";

export interface UploadItem {
  id: string;             // unique within a run, used as React key
  filename: string;
  size: number;
  contentType: string;
  status: ItemStatus;
  errorMessage?: string;
}

export interface ProgressEvent {
  items: UploadItem[];
}

export interface RunInput {
  blob: Blob;
  filename: string;
  contentType: string;
}

/**
 * Translate a `FakeFile[]` (from `buildFullScanPayload`) into the input
 * shape `runUploadPipeline` consumes. Tiny, but keeps the call sites tidy.
 */
export function fakeFilesToInput(files: FakeFile[]): RunInput[] {
  return files.map((f) => ({
    blob: f.blob,
    filename: f.filename,
    contentType: f.contentType,
  }));
}

/**
 * Translate a browser `File` (from a manual <input type="file">) into the
 * pipeline shape. Browser-supplied `file.type` is the source of truth; we
 * infer if missing.
 */
export function browserFileToInput(file: File): RunInput {
  return {
    blob: file,
    filename: file.name,
    contentType: file.type || inferContentType(file.name),
  };
}

/**
 * Retries per file after the first attempt, for transient faults only. Sized
 * against the presigned URL's lifetime: hezo-ops signs them for 600s, and the
 * worst case here is 3 attempts × 60s timeout + 1s + 2s backoff ≈ 183s per
 * file. The capture run is 13 files (6 squares, capture-meta.json, 6 full
 * frames), queued in that order at concurrency 4: every required file starts
 * behind at most one stuck slot, ≈ 366s, still inside the window. The full
 * frames come last and can outlive their URL in the worst case (a fourth file
 * in a slot starts after ≈ 549s); an expired URL answers 403, which is not
 * retried, and a missing full frame does not hold the scan back
 * (isOptionalUpload). The customer-facing retry mints fresh URLs instead.
 */
export const UPLOAD_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 1000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** One file, with exponential backoff between retryable failures. */
async function uploadWithRetry(
  url: string,
  inp: RunInput,
  opts: { retries: number; retryBaseDelayMs: number; timeoutMs?: number },
): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      await uploadFile(url, inp.blob, inp.contentType, { timeoutMs: opts.timeoutMs });
      return;
    } catch (err) {
      if (attempt >= opts.retries || !isRetryableUploadError(err)) throw err;
      await sleep(opts.retryBaseDelayMs * 2 ** attempt);
    }
  }
}

export interface RunResult {
  scanID: string;
  presignedPuts: Record<string, string>; // for debugging / display
  /**
   * Final per-item state, in input order. Individual PUT failures don't
   * throw, so this is the only place a caller can learn that the S3 prefix
   * is incomplete, and which filenames to re-request URLs for on a retry.
   */
  items: UploadItem[];
}

/**
 * Drive the full upload chain for one scan:
 *
 *   1. Build the `UploadItem[]` state from the declared filenames
 *   2. POST /presigned-uploads → map { filename: url }
 *   3. PUT each blob in parallel, updating per-item status
 *
 * Throws if step 2 fails (no presigned URLs → nothing to do). Individual
 * upload failures in step 3 are captured on the item, not thrown. They come
 * back on `RunResult.items` so the caller can re-run the pipeline with just
 * the failed inputs, which mints fresh presigned URLs for exactly those
 * filenames.
 */
export async function runUploadPipeline(opts: {
  baseUrl: string;
  token: string;
  scanID: string;
  inputs: RunInput[];
  concurrency?: number;
  onProgress?: (ev: ProgressEvent) => void;
  /** Overrides for tests; production uses the defaults. */
  retries?: number;
  retryBaseDelayMs?: number;
  timeoutMs?: number;
}): Promise<RunResult> {
  const {
    baseUrl,
    token,
    scanID,
    inputs,
    concurrency = 4,
    onProgress,
    retries = UPLOAD_RETRIES,
    retryBaseDelayMs = RETRY_BASE_DELAY_MS,
    timeoutMs,
  } = opts;

  const items: UploadItem[] = inputs.map((inp, i) => ({
    id: `${inp.filename}#${i}`,
    filename: inp.filename,
    size: inp.blob.size,
    contentType: inp.contentType,
    status: "pending",
  }));
  const emit = () => onProgress?.({ items: items.map((it) => ({ ...it })) });
  emit();

  // Step 1: ask hezo-ops for presigned URLs.
  const filenames = inputs.map((inp) => inp.filename);
  const { presignedPuts } = await requestPresignedUploads(baseUrl, token, filenames);

  // Step 2: PUT each file. Bounded parallelism via a simple worker pool.
  let cursor = 0;
  async function worker(): Promise<void> {
    while (cursor < items.length) {
      const idx = cursor++;
      const item = items[idx];
      const inp = inputs[idx];
      const presignedUrl = presignedPuts[item.filename];
      if (!presignedUrl) {
        item.status = "error";
        item.errorMessage = "Server did not return a presigned URL for this filename";
        emit();
        continue;
      }
      item.status = "uploading";
      emit();
      try {
        await uploadWithRetry(presignedUrl, inp, { retries, retryBaseDelayMs, timeoutMs });
        item.status = "done";
      } catch (err) {
        item.status = "error";
        item.errorMessage =
          err instanceof ApiError
            ? `${err.status} ${err.message} — ${err.body}`
            : (err as Error).message;
      }
      emit();
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
  await Promise.all(workers);

  return {
    scanID,
    presignedPuts,
    items: items.map((it) => ({ ...it })),
  };
}
